import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Save/restore scroll position across React Router navigations.
 *
 * Why the naïve `window.scrollTo(0, y)` doesn't work for recipe lists:
 * the list pages fetch their data asynchronously and render an empty
 * placeholder first. By the time we try to restore the scroll, the page
 * is only 0–600px tall, so the browser silently clamps the scroll to the
 * page's max height. We work around this by polling `document.body.scrollHeight`
 * for ~1.5s and re-applying the desired scroll position once the body is
 * tall enough to honour it.
 *
 * sessionStorage is intentional: positions reset when the app closes,
 * matching native Android/iOS behaviour.
 */
const KEY_PREFIX = 'matbakhi:scroll:';
const MAX_RESTORE_MS = 3000;
const POLL_MS = 50;

export default function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const key = KEY_PREFIX + location.pathname + location.search;

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    if (navigationType === 'POP') {
      const target = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (target > 0) {
        const start = Date.now();
        const tryRestore = () => {
          if (cancelled) return;
          // Required height for the target scroll to actually take effect.
          const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
          if (maxScroll >= target) {
            window.scrollTo(0, target);
            return;
          }
          window.scrollTo(0, Math.max(0, maxScroll));         // partial scroll while loading
          if (Date.now() - start < MAX_RESTORE_MS) {
            pollTimer = setTimeout(tryRestore, POLL_MS);
          }
        };
        // Defer to next frame so the new route has at least one paint.
        requestAnimationFrame(tryRestore);
      } else {
        window.scrollTo(0, 0);
      }
    } else {
      // Fresh navigation (PUSH/REPLACE) — start at top.
      window.scrollTo(0, 0);
    }

    const onScroll = () => {
      sessionStorage.setItem(key, String(window.scrollY));
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [key, navigationType]);

  return null;
}
